import * as React from 'react';
import { GITHUB_COPILOT_PREVIEW_PROVIDER_ID, GITHUB_COPILOT_PROVIDER_ID } from "./copilot-identity.js";
import { TURN_MODEL_PROVENANCE_KEY, isTurnModelProvenance, turnModelProvenanceDefinition } from "./turn-model-provenance.js";
import { TurnSelectionCard } from "./turn-selection-card.js";
import { RequestedModelsSchema } from "./turn-request-models.js";
import { TurnSelectionSchema } from "./turn-selection.js";
import { TURN_USAGE_EVIDENCE_KEY, isTurnUsageEvidence, turnUsageDiagnostic, turnUsageEvidenceDefinition } from "./turn-usage-evidence.js";
import { TurnUsageNotice } from "./turn-usage-notice.js";
import { TurnAccountViewSchema } from "./session-accounts-remote.js";
import { AutoAllocationCard } from "./auto-allocation-card.js";
export const AUTO_MODEL_ATTRIBUTION_KEY = 'github-copilot-auto-model-attribution';
const SLOT = 'conversation.chat.assistant-actions';
const noop = () => { };
function record(value) {
    return typeof value === 'object' && value !== null;
}
function sequence(value) {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}
function attribution(event) {
    if (!record(event) || event.type !== 'github-copilot/auto-model-decision' || !record(event.data)
        || !sequence(event.data.turn) || event.data.provider !== GITHUB_COPILOT_PREVIEW_PROVIDER_ID
        || typeof event.data.model !== 'string' || event.data.model.trim() === ''
        || !['fast', 'balanced', 'strong'].includes(String(event.data.taskClass))
        || event.data.preference !== undefined && !['efficiency', 'balance', 'intelligence'].includes(String(event.data.preference))
        || !['short-text-turn', 'standard-turn', 'large-structured-turn', 'image-capability'].includes(String(event.data.reason))
        || !sequence(event.data.candidateCount) || event.data.candidateCount < 1)
        return undefined;
    return {
        turn: event.data.turn,
        value: {
            provider: event.data.provider,
            model: event.data.model,
            ...event.data.preference === undefined ? {} : { preference: event.data.preference },
            taskClass: event.data.taskClass,
            reason: event.data.reason,
            candidateCount: event.data.candidateCount,
            ...sequence(event.data.fittingCandidateCount) ? { fittingCandidateCount: event.data.fittingCandidateCount } : {},
            ...sequence(event.data.estimatedInputTokens) ? { estimatedInputTokens: event.data.estimatedInputTokens } : {},
            ...sequence(event.data.selectedInputBudget) ? { selectedInputBudget: event.data.selectedInputBudget } : {},
            ...typeof event.data.inputFitDiagnostic === 'string' ? { inputFitDiagnostic: event.data.inputFitDiagnostic } : {},
        },
    };
}
export const autoModelAttributionDefinition = {
    kind: AUTO_MODEL_ATTRIBUTION_KEY,
    match(event) {
        const parsed = attribution(event);
        return parsed === undefined ? null : { id: String(parsed.turn), role: 'start' };
    },
    start(_context, match) {
        const parsed = attribution(match.event);
        if (parsed === undefined)
            throw new Error('COPILOT_AUTO_ATTRIBUTION_START');
        return parsed;
    },
    update(context) {
        return context.state;
    },
    buildLocationData(context, scope, previous) {
        if (scope !== 'turn')
            return null;
        const state = context.state ?? context.matches.map(match => attribution(match.event)).find(value => value !== undefined);
        if (state === undefined)
            return null;
        if (previous?.turn === state.turn && previous.key === AUTO_MODEL_ATTRIBUTION_KEY
            && previous.value.provider === state.value.provider && previous.value.model === state.value.model
            && previous.value.reason === state.value.reason && previous.value.taskClass === state.value.taskClass
            && previous.value.preference === state.value.preference
            && previous.value.candidateCount === state.value.candidateCount
            && previous.value.fittingCandidateCount === state.value.fittingCandidateCount
            && previous.value.inputFitDiagnostic === state.value.inputFitDiagnostic)
            return previous;
        return { kind: 'turn', turn: state.turn, key: AUTO_MODEL_ATTRIBUTION_KEY, value: state.value };
    },
};
const missingSource = { getSnapshot: () => undefined, subscribe: () => noop };
const failedSource = { ...missingSource, failed: true };
function sourceOf(props, key, diagnostic, failure = 'COPILOT_TURN_SELECTION_PROJECTION_FAILED') {
    const turn = props.turn;
    if (!record(turn) || !record(turn.data) || typeof turn.data.source !== 'function')
        return missingSource;
    try {
        const source = turn.data.source(key);
        return record(source) && typeof source.getSnapshot === 'function' && typeof source.subscribe === 'function'
            ? source : missingSource;
    }
    catch {
        diagnostic(failure);
        return failedSource;
    }
}
function iterable(value) {
    return value !== null && typeof value === 'object' && Symbol.iterator in value && typeof value[Symbol.iterator] === 'function';
}
function findTail(snapshot, messageId, diagnostic) {
    if (typeof messageId !== 'string')
        return undefined;
    if (!record(snapshot) || !record(snapshot.nodes) || typeof snapshot.nodes.values !== 'function') {
        diagnostic('COPILOT_TURN_SELECTION_CHAT_NODES_UNAVAILABLE');
        return undefined;
    }
    const nodes = snapshot.nodes.values();
    if (!iterable(nodes)) {
        diagnostic('COPILOT_TURN_SELECTION_CHAT_NODES_UNAVAILABLE');
        return undefined;
    }
    for (const node of nodes) {
        if (!record(node) || node.kind !== 'turn-tail' || !record(node.data) || !record(node.data.closing))
            continue;
        const closing = node.data.closing;
        if (record(closing.finalNode) && closing.finalNode.messageId === messageId && record(node.location))
            return node;
    }
    return undefined;
}
function Attribution(props) {
    const [live, setLive] = React.useState({ mode: 'unknown' });
    const [readState, setReadState] = React.useState('loading');
    const [readAttempt, setReadAttempt] = React.useState(0);
    const [requested, setRequested] = React.useState();
    const [modelsFailed, setModelsFailed] = React.useState(false);
    const [modelsAttempt, setModelsAttempt] = React.useState(0);
    const turn = record(props.turn) && sequence(props.turn.turn) ? props.turn.turn : undefined;
    const sessionId = typeof props.sessionId === 'string' ? props.sessionId : undefined;
    const [account, setAccount] = React.useState();
    const [accountFailed, setAccountFailed] = React.useState(false);
    const [accountAttempt, setAccountAttempt] = React.useState(0);
    const readAllocation = React.useMemo(() => sessionId === undefined || props.remote?.allocationSummary === undefined
        ? undefined : () => props.remote.allocationSummary(sessionId), [sessionId, props.remote]);
    React.useEffect(() => {
        setLive({ mode: 'unknown' });
        if (turn === undefined || sessionId === undefined || props.remote === undefined) {
            setReadState('failed');
            return;
        }
        setReadState('loading');
        let active = true;
        void props.remote.get(sessionId, turn).then(result => {
            if (!active)
                return;
            const parsed = result.ok ? TurnSelectionSchema.safeParse(result.value) : undefined;
            if (parsed?.success) {
                setLive(parsed.data);
                setReadState('ready');
            }
            else {
                setReadState('failed');
                props.diagnostic('COPILOT_TURN_SELECTION_READ_FAILED');
            }
        }, () => { if (active) {
            setReadState('failed');
            props.diagnostic('COPILOT_TURN_SELECTION_READ_FAILED');
        } });
        return () => { active = false; };
    }, [props.remote, sessionId, turn, props.diagnostic, readAttempt]);
    const language = React.useSyncExternalStore(props.locale ? listener => props.locale.subscribe(listener) : () => noop, () => props.locale?.getLocale().active ?? 'en', () => 'en');
    const source = sourceOf(props, AUTO_MODEL_ATTRIBUTION_KEY, props.diagnostic);
    const value = React.useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot);
    const provenanceSource = sourceOf(props, TURN_MODEL_PROVENANCE_KEY, props.diagnostic, 'COPILOT_TURN_MODEL_PROVENANCE_READ_FAILED');
    const provenance = React.useSyncExternalStore(provenanceSource.subscribe, provenanceSource.getSnapshot, provenanceSource.getSnapshot);
    const usageSource = sourceOf(props, TURN_USAGE_EVIDENCE_KEY, props.diagnostic, 'COPILOT_TURN_USAGE_PROJECTION_FAILED');
    const usageEvidence = React.useSyncExternalStore(usageSource.subscribe, usageSource.getSnapshot, usageSource.getSnapshot);
    const parsed = attribution({ type: 'github-copilot/auto-model-decision', data: { ...record(value) ? value : {}, turn: 0 } })?.value;
    const evidence = isTurnModelProvenance(provenance) ? provenance : undefined;
    const native = record(props.tail) && record(props.tail.data) ? props.tail.data : undefined;
    const nativeCompleted = native?.turn === turn && sequence(native?.seq);
    const completed = evidence?.ended === true || nativeCompleted;
    React.useEffect(() => {
        setAccount(undefined);
        setAccountFailed(false);
        if (turn === undefined || sessionId === undefined || props.accountRemote === undefined)
            return;
        let active = true;
        void props.accountRemote.turn(sessionId, turn).then(result => {
            if (!active)
                return;
            const parsed = result.ok ? TurnAccountViewSchema.safeParse(result.value) : undefined;
            if (parsed?.success)
                setAccount(parsed.data);
            else {
                setAccountFailed(true);
                props.diagnostic('COPILOT_TURN_ACCOUNT_READ_FAILED');
            }
        }, () => { if (active) {
            setAccountFailed(true);
            props.diagnostic('COPILOT_TURN_ACCOUNT_READ_FAILED');
        } });
        return () => { active = false; };
    }, [props.accountRemote, sessionId, turn, props.diagnostic, accountAttempt, completed]);
    const nativeRoutes = nativeCompleted && record(native?.tokenUsage) && Array.isArray(native.tokenUsage.routes)
        ? native.tokenUsage.routes : [];
    const recorded = evidence?.routes.length ? evidence.routes : nativeRoutes.filter((route) => record(route) && typeof route.provider === 'string' && route.provider.trim() !== ''
        && typeof route.model === 'string' && route.model.trim() !== '');
    const needsRequested = completed && recorded.length === 0;
    React.useEffect(() => {
        setRequested(undefined);
        setModelsFailed(false);
        if (!needsRequested || turn === undefined || sessionId === undefined)
            return;
        const read = props.remote?.requestedModels;
        if (!read) {
            setModelsFailed(true);
            props.diagnostic('COPILOT_TURN_REQUEST_REMOTE_UNAVAILABLE');
            return;
        }
        let active = true;
        void read(sessionId, turn).then(result => {
            if (!active)
                return;
            const parsed = result.ok ? RequestedModelsSchema.safeParse(result.value) : undefined;
            if (parsed?.success)
                setRequested(parsed.data);
            else {
                setModelsFailed(true);
                props.diagnostic('COPILOT_TURN_REQUEST_READ_FAILED');
            }
        }, () => { if (active) {
            setModelsFailed(true);
            props.diagnostic('COPILOT_TURN_REQUEST_READ_FAILED');
        } });
        return () => { active = false; };
    }, [needsRequested, turn, sessionId, props.remote, props.diagnostic, modelsAttempt]);
    if (!completed)
        return null;
    if (evidence === undefined)
        props.diagnostic('COPILOT_TURN_SELECTION_PROVENANCE_UNAVAILABLE');
    const routes = recorded.length ? recorded : requested?.incomplete === false ? requested.routes : [];
    const copilot = routes.some(route => record(route)
        && (route.provider === GITHUB_COPILOT_PREVIEW_PROVIDER_ID || route.provider === GITHUB_COPILOT_PROVIDER_ID)
        && typeof route.model === 'string' && route.model.trim() !== '');
    const usageDiagnostic = turn === undefined ? undefined : turnUsageDiagnostic(turn, native, usageEvidence, copilot);
    if (!copilot && parsed === undefined && live.mode === 'unknown' && usageDiagnostic === undefined)
        return null;
    if (usageDiagnostic !== undefined && !isTurnUsageEvidence(usageEvidence))
        props.diagnostic('COPILOT_TURN_USAGE_EVIDENCE_UNAVAILABLE');
    const selection = live.mode !== 'unknown' ? live : parsed?.preference ? {
        mode: 'auto', preference: parsed.preference, reason: parsed.reason,
        candidateCount: parsed.candidateCount, fittingCandidateCount: parsed.fittingCandidateCount,
    } : { mode: 'unknown' };
    const nativeCompleteModels = nativeCompleted && nativeRoutes.length > 0
        && nativeRoutes.every(route => record(route) && typeof route.provider === 'string' && route.provider.trim() !== ''
            && typeof route.model === 'string' && route.model.trim() !== '');
    const models = nativeCompleteModels ? undefined : recorded.length
        ? { kind: 'recorded', routes: recorded, incomplete: evidence?.incomplete ?? true }
        : requested?.incomplete === false && requested.routes.length
            ? { kind: 'requested', routes: requested.routes, incomplete: true }
            : { kind: 'unknown', routes: [], incomplete: true };
    return React.createElement(React.Fragment, null, React.createElement(TurnSelectionCard, { selection, locale: language, incomplete: nativeCompleteModels ? false : evidence?.incomplete ?? true,
        readState: selection.mode === 'unknown' ? readState : 'ready',
        models, modelsFailed: modelsFailed || provenanceSource.failed === true,
        retryModels: props.remote?.requestedModels === undefined && provenanceSource.failed !== true ? undefined : () => setModelsAttempt(value => value + 1),
        account, accountFailed, retryAccount: () => setAccountAttempt(value => value + 1),
        allocationEvidence: readAllocation === undefined ? undefined
            : React.createElement(AutoAllocationCard, { read: readAllocation, locale: language }),
        retry: props.remote === undefined ? undefined : () => setReadAttempt(value => value + 1) }), usageDiagnostic === undefined ? null : React.createElement(TurnUsageNotice, { diagnostic: usageDiagnostic, locale: language }));
}
export function installAutoModelProjections(capabilities) {
    if (!record(capabilities.uiConversation) || !record(capabilities.uiConversation.events)
        || typeof capabilities.uiConversation.events.register !== 'function') {
        capabilities.diagnostic('COPILOT_AUTO_PROJECTIONS_UNAVAILABLE');
        return noop;
    }
    const events = capabilities.uiConversation.events;
    let removeDefinition = noop;
    let removeProvenance = noop;
    let removeUsage = noop;
    try {
        removeDefinition = events.register(autoModelAttributionDefinition);
        removeProvenance = events.register(turnModelProvenanceDefinition);
    }
    catch {
        removeProvenance();
        removeDefinition();
        capabilities.diagnostic('COPILOT_AUTO_PROJECTIONS_FAILED');
        return noop;
    }
    try {
        removeUsage = events.register(turnUsageEvidenceDefinition);
    }
    catch {
        capabilities.diagnostic('COPILOT_TURN_USAGE_PROJECTION_FAILED');
    }
    return () => { removeUsage(); removeProvenance(); removeDefinition(); };
}
export function installAutoModelPresentation(capabilities) {
    const diagnosed = new Set();
    const diagnostic = (code) => {
        if (!diagnosed.has(code)) {
            diagnosed.add(code);
            capabilities.diagnostic(code);
        }
    };
    const slotsCandidate = capabilities.slots;
    if (!record(slotsCandidate) || !['spec', 'inject', 'register'].every(key => typeof slotsCandidate[key] === 'function')) {
        diagnostic('COPILOT_AUTO_PRESENTATION_UNAVAILABLE');
        return noop;
    }
    const slots = slotsCandidate;
    const face = record(capabilities.remote) ? capabilities.remote.githubCopilotTurnSelection : undefined;
    const getSelection = record(face) ? face.get : undefined;
    const getRequested = record(face) ? face.requestedModels : undefined;
    const getAllocation = record(face) ? face.allocationSummary : undefined;
    const remote = typeof getSelection === 'function' ? {
        get: (agentId, turn) => getSelection(agentId, turn),
        ...typeof getRequested === 'function' ? { requestedModels: (agentId, turn) => getRequested(agentId, turn) } : {},
        ...typeof getAllocation === 'function' ? { allocationSummary: (agentId) => getAllocation(agentId) } : {},
    } : undefined;
    const accountFace = record(capabilities.remote) ? capabilities.remote.githubCopilotSessionAccount : undefined;
    const readAccount = record(accountFace) ? accountFace.turn : undefined;
    const accountRemote = typeof readAccount === 'function'
        ? { turn: (agentId, turn) => readAccount(agentId, turn) } : undefined;
    const locale = record(capabilities.locale) && typeof capabilities.locale.getLocale === 'function'
        && typeof capabilities.locale.subscribe === 'function' ? capabilities.locale : undefined;
    if (!remote)
        diagnostic('COPILOT_TURN_SELECTION_REMOTE_UNAVAILABLE');
    function Entry(props) {
        if (typeof props.useChat !== 'function') {
            diagnostic('COPILOT_TURN_SELECTION_CHAT_RUNTIME_UNAVAILABLE');
            return null;
        }
        const useChat = props.useChat;
        const tail = useChat(snapshot => findTail(snapshot, props.messageId, diagnostic));
        const turn = record(tail) && record(tail.location) ? tail.location.turn : undefined;
        if (!record(turn) || !sequence(turn.turn))
            return null;
        return React.createElement(Attribution, { ...props, key: `${props.sessionId}:${turn.turn}`, turn, tail, remote, accountRemote, locale, diagnostic });
    }
    const removeProjections = capabilities.uiConversation === undefined ? noop
        : installAutoModelProjections({ uiConversation: capabilities.uiConversation, diagnostic });
    let removeInjection = noop;
    try {
        removeInjection = slots.inject(SLOT, () => {
            if (slots.spec(SLOT)?.kind !== 'list' || slots.spec(SLOT)?.scope !== 'session') {
                diagnostic('COPILOT_AUTO_ACTIONS_SLOT_UNAVAILABLE');
                return noop;
            }
            return slots.register({ name: SLOT, id: 'github-copilot-auto-model', order: 20 }, Entry);
        });
    }
    catch {
        removeInjection();
        removeProjections();
        diagnostic('COPILOT_AUTO_PRESENTATION_FAILED');
        return noop;
    }
    return () => {
        removeInjection();
        removeProjections();
    };
}
//# sourceMappingURL=auto-model-presentation.js.map